import { useEffect } from "react";

import { PLATFORM_TYPES } from "../offerPlan.constants";
import { currencyMin } from "../offersFormParams/offersFormParams";

import type { UseFormReturn } from "react-hook-form";

import type { ProviderPlan } from "@/src/api/OfferAPI.types";
import type { PlanFormValues } from "@/src/components/offers/offerPlanAdapter";

type CurrencyCode = keyof typeof currencyMin;

/**
 * Reactive port of the cross-field derivations that `formOnChange` used to
 * apply imperatively on every field change:
 * - currency change clamps price to the new currency's minimum (a below-min
 *   price typed by the user is left as-is and flagged by zod at submit)
 * - setting a limit date clears the rental duration (and vice versa)
 * - disabling geoblocking clears the selected countries
 * - changing platform resets provider_plans (paid plans) and the okast-only
 *   gift/donation toggles
 * - enabling monetization (AVOD) turns the D2G option off — the two are
 *   mutually exclusive, and AVOD wins
 *
 * These run off a single `watch` subscription rather than one `watch("field")`
 * + `useEffect` per rule: the subscription never re-renders the drawer, and
 * `type === "change"` isolates genuine user edits (including controlled fields,
 * whose `onChange` reports `type: "change"`) from `methods.reset()`/`setValue`
 * (which report no type) — so no `dirtyFields` gating is needed.
 */
export default function usePlanFormDerivations(
  methods: UseFormReturn<PlanFormValues>,
  availableProviderPlanList: ProviderPlan[],
): void {
  const { watch, setValue, getValues, clearErrors } = methods;
  const platform = watch("platform");
  const monetizationEnabled = watch("monetization_enabled");

  useEffect(() => {
    const subscription = watch((_values, { name, type }) => {
      if (type !== "change") return;
      switch (name) {
        case "currency": {
          const min = currencyMin[getValues("currency") as CurrencyCode];
          if (min !== undefined && getValues("price") < min) {
            setValue("price", min, { shouldDirty: true, shouldValidate: true });
          }
          break;
        }
        case "limit_date": {
          if (getValues("limit_date")) {
            setValue("rental_duration", 0, { shouldDirty: true });
          }
          break;
        }
        case "rental_duration": {
          if (getValues("rental_duration") !== 0) {
            setValue("limit_date", null, { shouldDirty: true });
          }
          break;
        }
        case "geoblock_mode": {
          if (getValues("geoblock_mode") === "disabled") {
            setValue("countries", [], { shouldDirty: true });
            setValue("country_groups", [], { shouldDirty: true });
          }
          break;
        }
        case "platform": {
          const isOkast = getValues("platform") === PLATFORM_TYPES.OKAST;
          // Gift/donation only apply on okast — re-normalize so the disabled
          // toggles never keep a stale enabled state.
          setValue("gift_enabled", isOkast, { shouldDirty: true });
          setValue("donation_enabled", false, { shouldDirty: true });
          // Paid plans track payment providers (okast → all available, else
          // none); free plans have none. Clear any stale provider error: the
          // field's relevance (and its value) just changed with the platform,
          // so a "payment method required" issue from a previous platform must
          // not linger.
          clearErrors("provider_plans");
          if (getValues("type") !== "free") {
            setValue(
              "provider_plans",
              isOkast ? availableProviderPlanList : undefined,
              { shouldDirty: true },
            );
          }
          break;
        }
        default:
          break;
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, getValues, clearErrors, availableProviderPlanList]);

  useEffect(() => {
    // A paid okast plan must keep at least one payment method: when a single
    // provider is configured it's mandatory, so force it selected (the toggle
    // is also locked in PlanPaymentField). Driven off the `platform` value, not
    // a change event — this must hold on initial load too, not just after a
    // user edit.
    if (platform !== PLATFORM_TYPES.OKAST) return;
    if (getValues("type") === "free") return;
    if (availableProviderPlanList.length !== 1) return;
    const onlyProvider = availableProviderPlanList[0];
    if (!onlyProvider) return;
    if (!(getValues("provider_plans") ?? []).includes(onlyProvider)) {
      setValue("provider_plans", [onlyProvider], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [platform, availableProviderPlanList, getValues, setValue]);

  useEffect(() => {
    if (!monetizationEnabled) return;
    if (!getValues("d2g_enabled")) return;
    setValue("d2g_enabled", false, { shouldDirty: true });
  }, [monetizationEnabled, getValues, setValue]);
}
